import { writeSceneLog } from '../utils/writeSceneLog.js';

var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), member.set(obj, value), value);
var _profileInProgress, _profileStartTs, _trailAnimationFrameId, _recordedTrailingSpans, _visibilityChangeHandler;
const POST_STORM_WINDOW = 2e3;
const SPAN_THRESHOLD = 30;
const TAB_INACTIVE_THRESHOLD = 1e3;
class SceneRenderProfiler {
  constructor(queryController) {
    this.queryController = queryController;
    __privateAdd(this, _profileInProgress, null);
    __privateAdd(this, _profileStartTs, null);
    __privateAdd(this, _trailAnimationFrameId, null);
    // Will keep measured lengths trailing frames
    __privateAdd(this, _recordedTrailingSpans, []);
    this.lastFrameTime = 0;
    __privateAdd(this, _visibilityChangeHandler, null);
    this.measureTrailingFrames = (measurementStartTs, lastFrameTime, profileStartTs) => {
      var _a;
      const currentFrameTime = performance.now();
      const frameLength = currentFrameTime - lastFrameTime;
      if (frameLength > TAB_INACTIVE_THRESHOLD) {
        writeSceneLog("SceneRenderProfiler", "Tab was inactive, cancelling profile measurement");
        this.cancelProfile();
        return;
      }
      __privateGet(this, _recordedTrailingSpans).push(frameLength);
      if (currentFrameTime - measurementStartTs < POST_STORM_WINDOW) {
        if (__privateGet(this, _profileInProgress)) {
          __privateSet(this, _trailAnimationFrameId, requestAnimationFrame(
            () => this.measureTrailingFrames(measurementStartTs, currentFrameTime, profileStartTs)
          ));
        }
      } else {
        const slowFrames = processRecordedSpans(__privateGet(this, _recordedTrailingSpans));
        const slowFramesTime = slowFrames.reduce((acc, val) => acc + val, 0);
        writeSceneLog(
          this.constructor.name,
          "Profile tail recorded, slow frames duration:",
          slowFramesTime,
          slowFrames,
          __privateGet(this, _profileInProgress)
        );
        __privateSet(this, _recordedTrailingSpans, []);
        const profileDuration = measurementStartTs - profileStartTs;
        writeSceneLog(
          this.constructor.name,
          "Stoped recording, total measured time (network included):",
          profileDuration + slowFramesTime
        );
        __privateSet(this, _trailAnimationFrameId, null);
        const profileEndTs = profileStartTs + profileDuration + slowFramesTime;
        if (!__privateGet(this, _profileInProgress)) {
          return;
        }
        performance.measure(`DashboardInteraction ${__privateGet(this, _profileInProgress).origin}`, {
          start: profileStartTs,
          end: profileEndTs
        });
        const networkDuration = captureNetwork(profileStartTs, profileEndTs);
        if (((_a = this.queryController) == null ? void 0 : _a.state.onProfileComplete) && __privateGet(this, _profileInProgress)) {
          this.queryController.state.onProfileComplete({
            origin: __privateGet(this, _profileInProgress).origin,
            crumbs: __privateGet(this, _profileInProgress).crumbs,
            duration: profileDuration + slowFramesTime,
            networkDuration,
            startTs: profileStartTs,
            endTs: profileEndTs,
            // @ts-ignore
            jsHeapSizeLimit: performance.memory ? performance.memory.jsHeapSizeLimit : 0,
            // @ts-ignore
            usedJSHeapSize: performance.memory ? performance.memory.usedJSHeapSize : 0,
            // @ts-ignore
            totalJSHeapSize: performance.memory ? performance.memory.totalJSHeapSize : 0
          });
          __privateSet(this, _profileInProgress, null);
          __privateSet(this, _trailAnimationFrameId, null);
        }
        if (window.__runs) {
          window.__runs += `${Date.now()}, ${profileDuration + slowFramesTime}
`;
        } else {
          window.__runs = `${Date.now()}, ${profileDuration + slowFramesTime}
`;
        }
      }
    };
    this.setupVisibilityChangeHandler();
  }
  setQueryController(queryController) {
    this.queryController = queryController;
  }
  setupVisibilityChangeHandler() {
    if (__privateGet(this, _visibilityChangeHandler)) {
      return;
    }
    __privateSet(this, _visibilityChangeHandler, () => {
      if (document.hidden && __privateGet(this, _profileInProgress)) {
        writeSceneLog("SceneRenderProfiler", "Tab became inactive, cancelling profile");
        this.cancelProfile();
      }
    });
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", __privateGet(this, _visibilityChangeHandler));
    }
  }
  cleanup() {
    if (__privateGet(this, _visibilityChangeHandler) && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", __privateGet(this, _visibilityChangeHandler));
      __privateSet(this, _visibilityChangeHandler, null);
    }
    this.cancelProfile();
  }
  startProfile(name) {
    if (__privateGet(this, _profileInProgress)) {
      this.addCrumb(name);
    } else {
      __privateSet(this, _profileInProgress, { origin: name, crumbs: [] });
      __privateSet(this, _profileStartTs, performance.now());
      writeSceneLog("SceneRenderProfiler", "Profile started:", __privateGet(this, _profileInProgress), __privateGet(this, _profileStartTs));
    }
  }
  recordProfileTail(measurementStartTime, profileStartTs) {
    __privateSet(this, _trailAnimationFrameId, requestAnimationFrame(
      () => this.measureTrailingFrames(measurementStartTime, measurementStartTime, profileStartTs)
    ));
  }
  tryCompletingProfile() {
    var _a;
    writeSceneLog("SceneRenderProfiler", "Trying to complete profile", __privateGet(this, _profileInProgress));
    if (((_a = this.queryController) == null ? void 0 : _a.runningQueriesCount()) === 0 && __privateGet(this, _profileInProgress)) {
      writeSceneLog("SceneRenderProfiler", "All queries completed, stopping profile");
      this.recordProfileTail(performance.now(), __privateGet(this, _profileStartTs));
    }
  }
  isTailRecording() {
    return Boolean(__privateGet(this, _trailAnimationFrameId));
  }
  cancelTailRecording() {
    if (__privateGet(this, _trailAnimationFrameId)) {
      cancelAnimationFrame(__privateGet(this, _trailAnimationFrameId));
      __privateSet(this, _trailAnimationFrameId, null);
      writeSceneLog("SceneRenderProfiler", "Cancelled recording frames, new profile started");
    }
  }
  // cancel profile
  cancelProfile() {
    if (__privateGet(this, _profileInProgress)) {
      writeSceneLog("SceneRenderProfiler", "Cancelling profile", __privateGet(this, _profileInProgress));
      __privateSet(this, _profileInProgress, null);
      if (__privateGet(this, _trailAnimationFrameId)) {
        cancelAnimationFrame(__privateGet(this, _trailAnimationFrameId));
        __privateSet(this, _trailAnimationFrameId, null);
      }
      __privateSet(this, _recordedTrailingSpans, []);
    }
  }
  addCrumb(crumb) {
    if (__privateGet(this, _profileInProgress)) {
      writeSceneLog("SceneRenderProfiler", "Adding crumb:", crumb);
      __privateGet(this, _profileInProgress).crumbs.push(crumb);
    }
  }
}
_profileInProgress = new WeakMap();
_profileStartTs = new WeakMap();
_trailAnimationFrameId = new WeakMap();
_recordedTrailingSpans = new WeakMap();
_visibilityChangeHandler = new WeakMap();
function processRecordedSpans(spans) {
  for (let i = spans.length - 1; i >= 0; i--) {
    if (spans[i] > SPAN_THRESHOLD) {
      return spans.slice(0, i + 1);
    }
  }
  return [spans[0]];
}
function captureNetwork(startTs, endTs) {
  const entries = performance.getEntriesByType("resource");
  performance.clearResourceTimings();
  const networkEntries = entries.filter(
    (entry) => entry.startTime >= startTs && entry.startTime <= endTs && entry.responseEnd >= startTs && entry.responseEnd <= endTs
  );
  for (const entry of networkEntries) {
    performance.measure("Network entry " + entry.name, {
      start: entry.startTime,
      end: entry.responseEnd
    });
  }
  return calculateNetworkTime(networkEntries);
}
function calculateNetworkTime(requests) {
  if (requests.length === 0) {
    return 0;
  }
  requests.sort((a, b) => a.startTime - b.startTime);
  let totalNetworkTime = 0;
  let currentStart = requests[0].startTime;
  let currentEnd = requests[0].responseEnd;
  for (let i = 1; i < requests.length; i++) {
    if (requests[i].startTime <= currentEnd) {
      currentEnd = Math.max(currentEnd, requests[i].responseEnd);
    } else {
      totalNetworkTime += currentEnd - currentStart;
      currentStart = requests[i].startTime;
      currentEnd = requests[i].responseEnd;
    }
  }
  totalNetworkTime += currentEnd - currentStart;
  return totalNetworkTime;
}
const REFRESH_INTERACTION = "refresh";
const TIME_RANGE_CHANGE_INTERACTION = "time_range_change";
const FILTER_REMOVED_INTERACTION = "filter_removed";
const FILTER_CHANGED_INTERACTION = "filter_changed";
const FILTER_RESTORED_INTERACTION = "filter_restored";
const VARIABLE_VALUE_CHANGED_INTERACTION = "variable_value_changed";
const SCOPES_CHANGED_INTERACTION = "scopes_changed";

export { FILTER_CHANGED_INTERACTION, FILTER_REMOVED_INTERACTION, FILTER_RESTORED_INTERACTION, REFRESH_INTERACTION, SCOPES_CHANGED_INTERACTION, SceneRenderProfiler, TIME_RANGE_CHANGE_INTERACTION, VARIABLE_VALUE_CHANGED_INTERACTION, calculateNetworkTime, captureNetwork, processRecordedSpans };
//# sourceMappingURL=SceneRenderProfiler.js.map
